import { Button, Image } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useFocusTrap } from '@mantine/hooks';
import { ExclamationCircleIcon, CheckCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { authRegister, resendVerification, usePasswordPolicy } from '@mygpt/app';
import type { PasswordRules } from '@mygpt/app';
import {
  buildPasswordValidator,
  getEnabledPasswordRuleKeys,
  getPasswordPolicyErrorMessage,
  isFreeEmailDomain,
  PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS,
} from '@mygpt/app/utils';
import logo from '@/assets/logo.png';

const emailRegex = /^\S+@\S+$/;

const Register = () => {
  const { t } = useTranslation();
  const focusTrapRef = useFocusTrap();
  // v0.9.3: 来自 website 的 ?redirect_uri= 透传到 /auth/login 链接，保留 promo 等 URL param
  const [searchParams] = useSearchParams();
  const redirectUriParam = searchParams.get('redirect_uri');
  const loginHref = redirectUriParam ? `/auth/login?redirect_uri=${redirectUriParam}` : '/auth/login';
  const [registerError, setRegisterError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const timerId = window.setTimeout(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => window.clearTimeout(timerId);
  }, [resendTimer]);

  const getErrorMessage = (error: any) =>
    (typeof error === 'object' && (error?.errorMsg || error?.message)) || t('register_page.error');

  // 密码策略:legacy(未配置/拉取失败)时保持现状校验,策略已配置时按规则预校验
  const { legacy: policyLegacy, rules: policyRules } = usePasswordPolicy();
  // Mantine useForm 的 validate 在初始化时捕获,用 ref 保证校验函数总能读到最新策略
  const policyRef = useRef<{ legacy: boolean; rules: PasswordRules | null }>({ legacy: true, rules: null });
  policyRef.current = { legacy: policyLegacy, rules: policyRules };
  const enabledRuleKeys = !policyLegacy && policyRules ? getEnabledPasswordRuleKeys(policyRules) : [];

  const form = useForm({
    initialValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validate: {
      name: (value) => (value.trim().length ? null : t('register_page.name_required')),
      email: (value) => {
        if (!value.trim()) {
          return t('fields.email_required');
        }
        if (!emailRegex.test(value.trim())) {
          return t('fields.email_invalid');
        }
        return isFreeEmailDomain(value) ? t('register_page.free_email_not_allowed') : null;
      },
      password: (value) => {
        const { legacy, rules } = policyRef.current;
        if (legacy || !rules) {
          // legacy 模式:保持现状(仅长度校验)
          return value.length < 8 ? t('login_password_min_length') : null;
        }
        const violations = buildPasswordValidator(rules)(value);
        return violations.length
          ? violations.map((v) => t(PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS[v])).join('; ')
          : null;
      },
      confirmPassword: (value, values) => (value === values.password ? null : t('fields.password_confirm_inequality')),
    },
    transformValues: (values) => ({
      name: values.name.trim(),
      email: values.email.trim(),
      password: values.password.trim(),
      confirmPassword: values.confirmPassword.trim(),
    }),
  });

  const nameInputProps = form.getInputProps('name', { withError: false });
  const emailInputProps = form.getInputProps('email', { withError: false });
  const passwordInputProps = form.getInputProps('password', { withError: false });
  const confirmPasswordInputProps = form.getInputProps('confirmPassword', { withError: false });

  const handleSubmit = async (
    values: { name: string; email: string; password: string; confirmPassword: string },
    event?: FormEvent<HTMLFormElement>
  ) => {
    event?.preventDefault();
    setRegisterError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      await authRegister({
        name: values.name,
        email: values.email.toLocaleLowerCase(),
        password: values.password,
      });
      setRegisteredEmail(values.email);
      setSuccessMessage(t('register_page.success'));
      setResendTimer(60);
      form.reset();
    } catch (error: any) {
      // 新错误码分支:PASSWORD_POLICY_VIOLATION / PASSWORD_REUSED(detail.code 判别)
      setRegisterError(getPasswordPolicyErrorMessage(error, t) || getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!registeredEmail || resendTimer > 0) return;
    setIsResending(true);
    setRegisterError('');
    try {
      await resendVerification({ email: registeredEmail });
      setSuccessMessage(t('register_page.verification_resent'));
      setResendTimer(60);
    } catch (error: any) {
      setRegisterError(
        (typeof error === 'object' && (error?.errorMsg || error?.message)) || t('register_page.resend_error')
      );
    } finally {
      setIsResending(false);
    }
  };

  const createAccountText = t('register_page.create_account');
  const descriptionText = t('register_page.description');
  const nameLabelText = t('register_page.name_label');
  const namePlaceholderText = t('register_page.name_placeholder');
  const confirmPasswordLabel = t('register_page.confirm_password');
  const confirmPasswordPlaceholder = t('register_page.confirm_password_placeholder');
  const alreadyHaveAccountText = t('register_page.have_account');
  const signInText = t('login.sign_in');
  const verificationInstructionText = t('register_page.verification_instruction', {
    email: registeredEmail,
  });
  const resendVerificationText = t('register_page.resend_email');
  const resendCountdownText = t('register_page.resend_countdown', {
    timer: resendTimer,
  });

  return (
    <div className="min-h-screen flex flex-col justify-center bg-gray-50 py-4 px-4 md:py-12 md:px-6 lg:px-8 relative">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Image src={logo} className="mx-auto h-10 w-auto md:h-12" fit="contain" />

        <h2 className="mt-4 md:mt-6 text-center text-2xl md:text-3xl font-bold tracking-tight text-gray-900">
          {createAccountText}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">{descriptionText}</p>
      </div>

      <div className="mt-6 md:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <form
          className="space-y-6 bg-white py-6 px-4 md:py-8 md:px-6 shadow rounded-xl border border-gray-200 sm:px-10"
          onSubmit={form.onSubmit(handleSubmit)}
          ref={focusTrapRef}
        >
          {registerError && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                <p className="ml-3 text-sm text-red-700">{registerError}</p>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <p className="text-sm text-green-700">{successMessage}</p>
                  {registeredEmail && (
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <p className="text-xs text-green-700">{verificationInstructionText}</p>
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={isResending || resendTimer > 0}
                        className="text-xs font-medium text-primary-700 hover:text-primary-600 disabled:opacity-60"
                      >
                        {resendTimer > 0 ? resendCountdownText : resendVerificationText}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              {nameLabelText}
            </label>
            <input
              id="name"
              type="text"
              autoComplete="name"
              placeholder={namePlaceholderText}
              className="block w-full rounded-xl border border-gray-300 px-4 py-3 md:py-3 text-gray-900 placeholder-gray-400 bg-gray-50 hover:border-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:bg-white transition-all duration-200 text-base md:text-sm"
              {...nameInputProps}
            />
            {form.errors.name && <p className="mt-2 text-sm text-red-600">{form.errors.name}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('login.work_email')}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t('login.work_email_placeholder')}
              className="block w-full rounded-xl border border-gray-300 px-4 py-3 md:py-3 text-gray-900 placeholder-gray-400 bg-gray-50 hover:border-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:bg-white transition-all duration-200 text-base md:text-sm"
              {...emailInputProps}
            />
            {form.errors.email && <p className="mt-2 text-sm text-red-600">{form.errors.email}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('login.password')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={t('login.password_placeholder')}
                className="block w-full rounded-xl border border-gray-300 px-4 py-3 md:py-3 text-gray-900 placeholder-gray-400 bg-gray-50 hover:border-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:bg-white transition-all duration-200 text-base md:text-sm"
                {...passwordInputProps}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center pr-4"
                aria-label={showPassword ? t('login.hide_password') : t('login.show_password')}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {enabledRuleKeys.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                {t('password_policy.hint_title')}{' '}
                {enabledRuleKeys.map((key) => t(PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS[key])).join('; ')}
              </p>
            )}
            {form.errors.password && <p className="mt-2 text-sm text-red-600">{form.errors.password}</p>}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              {confirmPasswordLabel}
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={confirmPasswordPlaceholder}
                className="block w-full rounded-xl border border-gray-300 px-4 py-3 md:py-3 text-gray-900 placeholder-gray-400 bg-gray-50 hover:border-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:bg-white transition-all duration-200 text-base md:text-sm"
                {...confirmPasswordInputProps}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center pr-4"
                aria-label={showConfirmPassword ? t('login.hide_password') : t('login.show_password')}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {form.errors.confirmPassword && <p className="mt-2 text-sm text-red-600">{form.errors.confirmPassword}</p>}
          </div>

          <Button
            type="submit"
            fullWidth
            size="md"
            radius="md"
            loading={isLoading}
            loaderProps={{ size: 'sm', color: 'white' }}
            className="bg-gray-900 hover:bg-gray-800 text-white font-medium text-base md:text-sm py-3.5 md:py-3 shadow-sm transition-colors duration-200"
            disabled={isLoading}
          >
            {t('login.sign_up')}
          </Button>
        </form>
      </div>

      <p className="mt-4 text-center text-sm text-gray-500">
        {alreadyHaveAccountText}{' '}
        <Link to={loginHref} className="font-semibold text-primary-700 hover:text-primary-600">
          {signInText}
        </Link>
      </p>
    </div>
  );
};

export default Register;
